import { useCallback, useReducer } from "react";
import type { BillingInfo, BusinessBillingInfoUpdate } from "lens-platform-sdk";

import type { BillingDetailsForm, FormUpdateParams, RecurlyData, CCFormErrors } from "../types";
import { billingDetailsFormDefaults, mapFormToBillingInfoUpdate } from "../utils";

type FormAction =
  | { type: "UPDATE_FIELD"; payload: FormUpdateParams }
  | { type: "SET_FORM_VALUES"; payload: BillingDetailsForm }
  | { type: "SET_SUBMITTING"; payload: boolean }
  | { type: "SET_FORM_ERRORS"; payload?: CCFormErrors };

type FormState = {
  values: BillingDetailsForm;
  isSubmitting: boolean;
  errors?: CCFormErrors;
};

const initialState: FormState = {
  values: billingDetailsFormDefaults,
  isSubmitting: false,
  errors: undefined,
};

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case "UPDATE_FIELD":
      const { key, value, valid } = action.payload;

      return {
        ...state,
        values: {
          ...state.values,
          [key]: {
            ...state.values[key],
            value,
            valid,
          },
        },
      };
    case "SET_FORM_VALUES":
      return {
        ...state,
        values: action.payload,
      };
    case "SET_SUBMITTING":
      return {
        ...state,
        isSubmitting: action.payload,
      };
    case "SET_FORM_ERRORS":
      return {
        ...state,
        errors: action.payload,
      };
    default:
      return state;
  }
}

export const useBillingForm = (
  billingInfo: BillingInfo | null | undefined,
  update: (data: BusinessBillingInfoUpdate) => void,
) => {
  const [formState, dispatch] = useReducer(formReducer, initialState);
  const updateField = useCallback((params: FormUpdateParams): void => {
    dispatch({ type: "UPDATE_FIELD", payload: params });
  }, []);

  const setFormValues = useCallback((newValues: BillingDetailsForm): void => {
    dispatch({ type: "SET_FORM_VALUES", payload: newValues });
  }, []);

  const setIsSubmitting = useCallback((isSubmitting: boolean): void => {
    dispatch({ type: "SET_SUBMITTING", payload: isSubmitting });
  }, []);

  const setFormErrors = useCallback((errors?: CCFormErrors): void => {
    dispatch({ type: "SET_FORM_ERRORS", payload: errors });
  }, []);

  const submitForm = useCallback(
    async (recurlyData?: RecurlyData) => {
      const data = mapFormToBillingInfoUpdate(formState.values, recurlyData, billingInfo?.paymentMethod);

      await update(data);

      return data;
    },
    [formState.values, billingInfo, update],
  );

  return {
    formValues: formState.values,
    setFormValues,
    isSubmitting: formState.isSubmitting,
    setIsSubmitting,
    formErrors: formState.errors,
    setFormErrors,
    updateField,
    submitForm,
  };
};
